// @flow

import { get } from 'lodash';
import memoizeOne from 'memoize-one';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Icon, ThemeContext, Button } from 'react-native-elements';
import { Text } from 'react-native';
import * as Yup from 'yup';
import FieldWrapper from './FieldWrapper';
import { useFormattedFieldErrors } from './utils';

const styles = memoizeOne((theme) => ({
    label: {
        fontSize: theme.fontsizes.categoryTitle,
        color: theme.colors.defaultTextColor,
        marginHorizontal: 10,
        fontWeight: 'bold',
    },
    error: {
        color: theme.colors.danger,
        marginHorizontal: 20,
        fontSize: theme.fontsizes.textSmall,
    },
    radioButtonView: {
        marginHorizontal: 25,
        borderRadius: 10,
        borderColor: theme.colors.lightgray,
        borderWidth: 2,
        paddingHorizontal: 40,
        paddingVertical: 10,
        marginVertical: 5,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignContent: 'center',
        alignItems: 'center',
    },
    radioButtonViewSelected: {
        backgroundColor: theme.colors.touchableUnderlay,
    },
    radioButtonViewNotSelected: {
        backgroundColor: theme.colors.background,
    },
    radioButtonText: {
        color: theme.colors.defaultTextColor,
        fontSize: theme.fontsizes.text,
    },
}));

type Props = {
    field: {
        value: any,
        name: string,
    },
    form: {
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
        handleChange: (name: string) => (value: string | null) => void,
        handleBlur: (name: string) => () => void,
        setFieldValue: (name: string, value: string) => () => void,
    },
    required: boolean,
    label: string,
    help_text?: string,
    disabled?: boolean,
    choices: Object,
    many: boolean,
};

const messages = defineMessages({
    required: {
        id: 'baseSelect-required',
        defaultMessage: 'This field is required.',
    },
});

const choicesValues = memoizeOne(Object.keys);
const processChoices = memoizeOne((choices) => choicesValues(choices).map((key) => ({
    id: key,
    name: choices[key],
    selected: false,
})));

const ExpandedSelect = (props: Props) => {
    const {
        disabled,
        field: {
            name, value,
        },
        form: {
            errors, touched,
            handleChange, handleBlur,
        },
        label,
        help_text: helpText,
        required,
        choices,
        many,
    } = props;

    const { theme } = React.useContext(ThemeContext);

    let errs = get(errors, name);
    errs = useFormattedFieldErrors(errs);

    const [itemsArr, setItemsArr] = React.useState(processChoices(choices));

    const onSelect = React.useCallback((index) => (
        () => {
            //Update shown items array
            const newItemsArr = [...itemsArr];
            const clickedItem = {...newItemsArr[index]};
            if (!many) {
                newItemsArr.forEach((item) => item.selected = false);
            }
            clickedItem.selected = !clickedItem.selected;
            newItemsArr[index] = clickedItem;
            setItemsArr(newItemsArr);

            //Update form
            let newValue = many ? value ? [...value] : [] : null;
            if (clickedItem.selected) {
                //Add item
                {
                    many
                        ? newValue.push(clickedItem.id)
                        : newValue = clickedItem.id
                }
            } else {
                //Remove item
                {
                    many
                        ? newValue = newValue.filter(item => item !== clickedItem.id)
                        : newValue = null
                }
            }
            handleChange(name)(newValue);
            handleBlur(name);
        }
    ), [itemsArr, value, many, name]);

    return (
        <FieldWrapper
            required={required}
            helpText={helpText}
        >
            <Text style={styles(theme).label}>{required ? `${label} *` : label}</Text>

            {itemsArr.map((item, index) => (
                <Button
                    key={item.id}
                    buttonStyle={[styles(theme).radioButtonView, item.selected
                        ? styles(theme).radioButtonViewSelected
                        : styles(theme).radioButtonViewNotSelected]}
                    title={item.name}
                    titleStyle={styles(theme).radioButtonText}
                    icon={
                        <Icon
                            type="material"
                            name={item.selected ? "radio-button-checked" : "radio-button-unchecked"}
                            size={35}
                            color={item.selected ? theme.colors.success : theme.colors.gray}
                        />
                    }
                    iconRight
                    onPress={onSelect(index)}
                />
            ))}

            {touched[name] && errs ? <Text style={styles(theme).error}>{errs}</Text> : null}
        </FieldWrapper>
    );
};

ExpandedSelect.getValidationSchema = (field: { validation: Object }) => {
    if (field.validation.required) {
        return Yup.string().required(messages.required).nullable();
    }

    return Yup.string().nullable();
};

ExpandedSelect.defaultProps = {
    disabled: false,
    help_text: '',
};

export default ExpandedSelect;
